import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';

// ─── Lazy-loaded pages ────────────────────────────────────────────────────────
const LoginPage        = lazy(() => import('./components/Login'));
const Dashboard        = lazy(() => import('./pages/Dashboard'));
const SendSms          = lazy(() => import('./components/SendSms'));
const VerificationForm = lazy(() => import('./pages/VerificationForm'));
const Expired          = lazy(() => import('./pages/Expired'));

// ─── Auth helpers ─────────────────────────────────────────────────────────────
function getAuth() {
  try {
    const token = localStorage.getItem('authToken');
    const user  = JSON.parse(localStorage.getItem('user') || 'null');
    return { token, user };
  } catch {
    return { token: null, user: null };
  }
}

// ─── Full-screen loading fallback ─────────────────────────────────────────────
function PageLoader() {
  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <div style={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        border: '3px solid #e5e7eb',
        borderTopColor: '#3b82f6',
        animation: 'spin 0.7s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── Protected route (must be logged in) ─────────────────────────────────────
// If no token → redirect to login.
// If token exists → render children.
function PrivateRoute() {
  const { token } = getAuth();
  return token ? <Outlet /> : <Navigate to="/" replace />;
}

// ─── Guest route (login page) ─────────────────────────────────────────────────
// If already logged in → redirect to dashboard.
// Otherwise → show login.
function GuestRoute() {
  const { token } = getAuth();
  return token ? <Navigate to="/dashboard" replace /> : <Outlet />;
}

// ─── App ──────────────────────────────────────────────────────────────────────
function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>

          {/* Public routes — no login needed */}
          <Route path="/verify/:token" element={<VerificationForm />} />
          <Route path="/expired"       element={<Expired />} />

          {/* Guest-only route — redirect to /dashboard if already logged in */}
          <Route element={<GuestRoute />}>
            <Route path="/" element={<LoginPage />} />
          </Route>

          {/* Protected routes — redirect to / if not logged in */}
          <Route element={<PrivateRoute />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/send-sms"  element={<SendSms />} />
          </Route>

          {/* Fallback — unknown paths */}
          <Route path="*" element={<Navigate to="/" replace />} />

        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;